import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useUser } from '../context/UserContext';
import { createRazorpayOrder, createCodOrder, verifyRazorpayPayment, getRazorpayConfig, DuplicateCheckResponse } from '../api/payment';
import { checkShippingServiceability } from '../api/shipping';
import PincodeChecker from '../components/PincodeChecker';
import AuthModal from '../components/AuthModal';
import AddressModal from '../components/AddressModal';
import DuplicateOrderModal from '../components/DuplicateOrderModal';
import SEO from '../components/SEO';
import { CreditCard, Banknote, ArrowLeft, CheckCircle, ShoppingBag, MapPin, Loader2, Truck, Trash2, Plus, Minus } from 'lucide-react';
import { updateUserProfile } from '../api/userAuth';

const CheckoutPage: React.FC = () => {
  const { state, clearCart, removeFromCart, updateQuantity } = useCart();
  const { user, token, login } = useUser();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<'Prepaid' | 'COD'>('Prepaid');
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const paymentErrorRef = useRef<HTMLDivElement>(null);
  const [etd, setEtd] = useState<string | null>(null);
  // Modal states
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showAddressModal, setShowAddressModal] = useState(false);

  // Duplicate Order Warning Modal state
  const [duplicateInfo, setDuplicateInfo] = useState<DuplicateCheckResponse | null>(null);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);

  const [shippingDetails, setShippingDetails] = useState({
    phone: user?.phone || '',
    street: user?.address?.street || '',
    city: user?.address?.city || '',
    state: user?.address?.state || '',
    zip: user?.address?.zip || '',
    country: user?.address?.country || 'India',
  });

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, []);

  useEffect(() => {
    if (paymentError) {
      requestAnimationFrame(() => {
        paymentErrorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        paymentErrorRef.current?.focus({ preventScroll: true });
      });
    }
  }, [paymentError]);

  useEffect(() => {
    if (user) {
      setShippingDetails({
        phone: user.phone || '',
        street: user.address?.street || '',
        city: user.address?.city || '',
        state: user.address?.state || '',
        zip: user.address?.zip || '',
        country: user.address?.country || 'India',
      });
    }
  }, [user]);

  useEffect(() => {
    // If cart is empty and not just successfully paid, redirect to home
    if (state.items.length === 0 && !paymentSuccess) {
      navigate('/products');
    }
  }, [state.items.length, paymentSuccess, navigate]);

  useEffect(() => {
    if (user?.address?.zip) {
      // Using a dummy pickup pincode (e.g., 560001) for now
      checkShippingServiceability('560001', user.address.zip)
        .then(res => {
          if (res.data?.data?.available_courier_companies?.length > 0) {
            setEtd(res.data.data.available_courier_companies[0].etd);
          }
        })
        .catch(console.error);
    }
  }, [user]);

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handleCheckout = async (
    confirmDuplicate: boolean = false,
    checkoutDetails = shippingDetails
  ) => {
    if (!user || !token) {
      setPaymentError(null);
      setShowAuthModal(true);
      return;
    }

    if (!checkoutDetails.street || !checkoutDetails.city || !checkoutDetails.zip) {
      setPaymentError(null);
      setShowAddressModal(true);
      return;
    }

    // Input Validations
    if (!checkoutDetails.phone.trim()) {
      setPaymentError('Phone number is required.');
      return;
    }
    const phoneDigits = checkoutDetails.phone.replace(/[^0-9]/g, '');
    const normalizedPhone = phoneDigits.length === 12 && phoneDigits.startsWith('91')
      ? phoneDigits.slice(2)
      : phoneDigits;
    if (!/^[6-9]\d{9}$/.test(normalizedPhone)) {
      setPaymentError('Please enter a valid 10-digit Indian mobile number.');
      return;
    }
    if (!checkoutDetails.street.trim()) {
      setPaymentError('Street address is required.');
      return;
    }
    if (!checkoutDetails.city.trim()) {
      setPaymentError('City is required.');
      return;
    }
    if (!checkoutDetails.state.trim()) {
      setPaymentError('State / Province is required.');
      return;
    }
    if (!checkoutDetails.zip.trim()) {
      setPaymentError('ZIP / Postal Code is required.');
      return;
    }
    if (!checkoutDetails.country.trim()) {
      setPaymentError('Country is required.');
      return;
    }

    try {
      setLoading(true);
      setPaymentError(null);

      // Save user details to profile before payment
      const updateProfileRes = await updateUserProfile(token, {
        firstName: user.firstName,
        lastName: user.lastName,
        phone: normalizedPhone,
        address: {
          street: checkoutDetails.street,
          city: checkoutDetails.city,
          state: checkoutDetails.state,
          zip: checkoutDetails.zip,
          country: checkoutDetails.country,
        }
      });

      // Sync local context with updated user details
      login(token, updateProfileRes.user);

      // Map cart items for backend
      const items = state.items.map(item => ({
        productId: item.product.id.toString(),
        quantity: item.quantity,
        selectedColor: item.selectedColor
      }));

      const shippingAddress = {
        firstName: user.firstName,
        lastName: user.lastName,
        phone: normalizedPhone,
        street: checkoutDetails.street,
        city: checkoutDetails.city,
        state: checkoutDetails.state,
        zip: checkoutDetails.zip,
        country: checkoutDetails.country,
      };

      if (paymentMethod === 'COD') {
        const createCodRes = await createCodOrder(items, shippingAddress, token, confirmDuplicate);
        if (!confirmDuplicate && createCodRes.hasDuplicate) {
          setDuplicateInfo(createCodRes as DuplicateCheckResponse);
          setShowDuplicateModal(true);
          setLoading(false);
          return;
        }

        if (createCodRes.success && createCodRes.order) {
          setPaymentSuccess(true);
          clearCart();
          navigate(`/order/${createCodRes.order.id}`);
        } else {
          setPaymentError(createCodRes.message || 'Failed to place COD order.');
        }
        return;
      }

      const createOrderRes = await createRazorpayOrder(items, shippingAddress, token, confirmDuplicate);
      if (!confirmDuplicate && createOrderRes.hasDuplicate) {
        setDuplicateInfo(createOrderRes as DuplicateCheckResponse);
        setShowDuplicateModal(true);
        setLoading(false);
        return;
      }

      if (!createOrderRes.success || !createOrderRes.order) {
        setPaymentError('Could not create order on the server.');
        setLoading(false);
        return;
      }

      const isLoaded = await loadRazorpayScript();
      if (!isLoaded) {
        setPaymentError('Razorpay SDK failed to load. Are you online?');
        setLoading(false);
        return;
      }
      if (!createOrderRes.success || !createOrderRes.order) {
        setPaymentError('Could not create order on the server.');
        setLoading(false);
        return;
      }

      let razorpayKey = 'rzp_test_Rjul01wbs88mD1';
      try {
        const config = await getRazorpayConfig();
        razorpayKey = config.keyId;
      } catch (err) {
        console.warn('Could not fetch Razorpay key from backend, using fallback', err);
      }

      const options = {
        key: razorpayKey, 
        amount: createOrderRes.order.amount,
        currency: createOrderRes.order.currency,
        name: 'c nine',
        description: 'Cart Checkout',
        order_id: createOrderRes.order.id,
        handler: async function (response: any) {
          try {
            const verifyRes = await verifyRazorpayPayment(
              response.razorpay_order_id,
              response.razorpay_payment_id,
              response.razorpay_signature
            );
            
            if (verifyRes.success && createOrderRes.order) {
              setPaymentSuccess(true);
              clearCart();
              // Navigate to the order detail page on success
              navigate(`/order/${createOrderRes.order.id}`);
            } else {
              setPaymentError('Payment verification failed.');
            }
          } catch (err: any) {
            setPaymentError(err.message || 'Payment verification failed.');
          }
        },
        prefill: {
          name: `${user.firstName} ${user.lastName}`,
          email: user.email,
          contact: shippingDetails.phone,
        },
        theme: {
          color: '#dc2626',
        },
      };

      const paymentObject = new (window as any).Razorpay(options);
      
      paymentObject.on('payment.failed', function (response: any) {
        setPaymentError(response.error.description || 'Payment failed.');
      });
      
      paymentObject.open();
    } catch (err: any) {
      setPaymentError(err.message || 'Something went wrong while initiating payment.');
    } finally {
      setLoading(false);
    }
  };

  if (paymentSuccess) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4">
        <SEO title="Order Success" />
        <div className="bg-white p-8 rounded-2xl shadow-lg max-w-md w-full text-center">
          <CheckCircle className="w-20 h-20 text-green-500 mx-auto mb-6" />
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Payment Successful!</h2>
          <p className="text-gray-600 mb-8">
            Thank you for shopping with us. Your order has been placed successfully and will be processed shortly.
          </p>
          <Link
            to="/products"
            className="inline-block bg-primary-600 hover:bg-primary-700 text-white py-3 px-8 rounded-lg font-semibold transition-colors"
          >
            Continue Shopping
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-8 pb-16">
      <SEO title="Checkout" />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        
        <Link to="/products" className="inline-flex items-center text-gray-600 hover:text-primary-600 transition-colors mb-6 font-medium">
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back to Shopping
        </Link>
        
        <h1 className="text-3xl font-bold text-gray-900 mb-8">Checkout</h1>

        {paymentError && (
          <div ref={paymentErrorRef} tabIndex={-1} className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 text-red-700 rounded-r-lg font-medium outline-none">
            {paymentError}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          
          {/* Form and Summary */}
          <div className="lg:col-span-2 space-y-6">
            {/* Shipping & Contact Info Form */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h2 className="text-xl font-semibold text-gray-900 border-b border-gray-100 pb-4 mb-6 flex items-center">
                <MapPin className="w-5 h-5 mr-2 text-primary-600 animate-pulse" /> Shipping & Contact Info
              </h2>
              
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                    <input
                      type="text"
                      disabled
                      value={user?.firstName || ''}
                      className="w-full px-4 py-2.5 border border-gray-200 bg-gray-50 rounded-lg text-gray-500 cursor-not-allowed text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                    <input
                      type="text"
                      disabled
                      value={user?.lastName || ''}
                      className="w-full px-4 py-2.5 border border-gray-200 bg-gray-50 rounded-lg text-gray-500 cursor-not-allowed text-sm"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    <input
                      type="email"
                      disabled
                      value={user?.email || ''}
                      className="w-full px-4 py-2.5 border border-gray-200 bg-gray-50 rounded-lg text-gray-500 cursor-not-allowed text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Phone Number <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      required
                      value={shippingDetails.phone}
                      onChange={(e) => setShippingDetails({ ...shippingDetails, phone: e.target.value })}
                      placeholder="Enter 10-digit phone number"
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-sm"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Street Address <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={shippingDetails.street}
                    onChange={(e) => setShippingDetails({ ...shippingDetails, street: e.target.value })}
                    placeholder="Apartment, suite, unit, building, street, etc."
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-sm"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      City <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={shippingDetails.city}
                      onChange={(e) => setShippingDetails({ ...shippingDetails, city: e.target.value })}
                      placeholder="City"
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      State / Province <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={shippingDetails.state}
                      onChange={(e) => setShippingDetails({ ...shippingDetails, state: e.target.value })}
                      placeholder="State"
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-sm"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      ZIP / Postal Code <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={shippingDetails.zip}
                      onChange={(e) => setShippingDetails({ ...shippingDetails, zip: e.target.value })}
                      placeholder="PIN Code"
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Country <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={shippingDetails.country}
                      onChange={(e) => setShippingDetails({ ...shippingDetails, country: e.target.value })}
                      placeholder="Country"
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-sm"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Cart Items */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <div className="flex items-center justify-between border-b border-gray-100 pb-4 mb-4">
                <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                  <ShoppingBag className="w-5 h-5 mr-2 text-primary-600" /> Order Summary
                </h2>
                {state.items.length > 0 && (
                  <button
                    onClick={() => {
                      if (window.confirm('Are you sure you want to clear your cart?')) {
                        clearCart();
                      }
                    }}
                    className="text-xs text-red-500 hover:text-red-700 hover:underline font-medium"
                    title="Clear all cart items"
                  >
                    Clear Cart
                  </button>
                )}
              </div>
              
              <div className="space-y-6">
                {state.items.map((item) => {
                  const fallbackImage = 'https://via.placeholder.com/150';
                  const itemImage = item.selectedColorVariant?.image || (item.selectedColor && item.product.colorVariants?.find(v => v.name === item.selectedColor)?.image) || item.product.image || fallbackImage;
                  const itemPrice = (item.selectedColorVariant?.price !== undefined && item.selectedColorVariant.price > 0) ? item.selectedColorVariant.price : item.product.price;
                  const itemKey = `${item.product.id}-${item.selectedColor || 'default'}`;

                  return (
                    <div key={itemKey} className="flex space-x-4 items-start border-b border-gray-100 pb-4 last:border-0 last:pb-0">
                      <img
                        src={itemImage}
                        alt={item.product.title}
                        className="w-20 h-20 sm:w-24 sm:h-24 object-cover rounded-lg border border-gray-100 flex-shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex justify-between items-start">
                          <div>
                            <h3 className="text-base sm:text-lg font-medium text-gray-900 line-clamp-2">
                              {item.product.title}
                            </h3>
                            {item.selectedColor && (
                              <span className="inline-block text-xs font-semibold text-indigo-700 bg-indigo-50 border border-indigo-100 px-2 py-0.5 rounded mt-1">
                                Color: {item.selectedColor}
                              </span>
                            )}
                          </div>
                          <button
                            onClick={() => removeFromCart(item.product.id, item.selectedColor)}
                            className="text-gray-400 hover:text-red-600 transition-colors ml-2 p-1 rounded-full hover:bg-red-50 flex-shrink-0"
                            title="Remove item"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                        <p className="text-primary-600 font-bold mt-1">₹{itemPrice}</p>
                        
                        <div className="flex items-center justify-between mt-3">
                          <div className="flex items-center space-x-3 bg-gray-50 border border-gray-200 rounded-lg px-2.5 py-1">
                            <button
                              onClick={() => updateQuantity(item.product.id, item.quantity - 1, item.selectedColor)}
                              className="text-gray-500 hover:text-gray-700 transition-colors p-0.5"
                              title="Decrease quantity"
                            >
                              <Minus className="w-4 h-4" />
                            </button>
                            <span className="text-sm font-semibold w-6 text-center text-gray-900">{item.quantity}</span>
                            <button
                              onClick={() => updateQuantity(item.product.id, item.quantity + 1, item.selectedColor)}
                              className="text-gray-500 hover:text-gray-700 transition-colors p-0.5"
                              title="Increase quantity"
                            >
                              <Plus className="w-4 h-4" />
                            </button>
                          </div>
                          <span className="text-sm font-bold text-gray-900">
                            Subtotal: ₹{itemPrice * item.quantity}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Payment Summary */}
          <div className="lg:col-span-1 space-y-6">
            <PincodeChecker defaultPincode={user?.address?.zip} />

            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 sticky top-24">
              <h2 className="text-xl font-semibold text-gray-900 border-b border-gray-100 pb-4 mb-4">Payment Details</h2>
              
              <div className="space-y-3 text-sm sm:text-base text-gray-600 mb-6">
                <div className="flex justify-between">
                  <span>Subtotal ({state.totalItems} items)</span>
                  <span className="font-medium text-gray-900">₹{state.totalPrice}</span>
                </div>
                <div className="flex justify-between">
                  <span>Shipping</span>
                  <span className="text-green-600 font-medium">Free</span>
                </div>
                {etd && (
                  <div className="flex justify-between text-sm text-gray-500 mt-2">
                    <span className="flex items-center"><Truck className="w-4 h-4 mr-1" /> Est. Delivery</span>
                    <span>{etd}</span>
                  </div>
                )}
                <div className="border-t border-gray-100 pt-3 mt-3 flex justify-between items-center text-lg sm:text-xl font-bold text-gray-900">
                  <span>Total</span>
                  <span>₹{state.totalPrice}</span>
                </div>
              </div>

              {/* Payment Method Selector */}
              <div className="mb-6 space-y-3">
                <label className="block text-sm font-semibold text-gray-900 mb-2">Select Payment Method</label>
                
                <div
                  onClick={() => setPaymentMethod('Prepaid')}
                  className={`flex items-start p-3.5 border rounded-xl cursor-pointer transition-all ${
                    paymentMethod === 'Prepaid'
                      ? 'border-primary-600 bg-primary-50/30 ring-2 ring-primary-600/20'
                      : 'border-gray-200 hover:border-gray-300 bg-white'
                  }`}
                >
                  <input
                    type="radio"
                    name="paymentMethod"
                    value="Prepaid"
                    checked={paymentMethod === 'Prepaid'}
                    onChange={() => setPaymentMethod('Prepaid')}
                    className="mt-1 text-primary-600 focus:ring-primary-500"
                  />
                  <div className="ml-3 flex-1">
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-gray-900 text-sm flex items-center">
                        <CreditCard className="w-4 h-4 mr-1.5 text-primary-600" />
                        Online Payment
                      </span>
                      <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">Fastest</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">UPI, Cards, Netbanking (Razorpay)</p>
                  </div>
                </div>

                <div
                  onClick={() => setPaymentMethod('COD')}
                  className={`flex items-start p-3.5 border rounded-xl cursor-pointer transition-all ${
                    paymentMethod === 'COD'
                      ? 'border-primary-600 bg-primary-50/30 ring-2 ring-primary-600/20'
                      : 'border-gray-200 hover:border-gray-300 bg-white'
                  }`}
                >
                  <input
                    type="radio"
                    name="paymentMethod"
                    value="COD"
                    checked={paymentMethod === 'COD'}
                    onChange={() => setPaymentMethod('COD')}
                    className="mt-1 text-primary-600 focus:ring-primary-500"
                  />
                  <div className="ml-3 flex-1">
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-gray-900 text-sm flex items-center">
                        <Banknote className="w-4 h-4 mr-1.5 text-emerald-600" />
                        Cash on Delivery (COD)
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">Pay cash upon delivery via Shiprocket</p>
                  </div>
                </div>
              </div>

              <button
                onClick={() => handleCheckout(false)}
                disabled={loading || state.items.length === 0}
                className={`w-full bg-primary-600 hover:bg-primary-700 text-white py-4 px-6 rounded-lg font-semibold transition-colors flex items-center justify-center text-lg shadow-md ${loading ? 'opacity-75 cursor-not-allowed' : ''}`}
              >
                {paymentMethod === 'COD' ? (
                  <Banknote className="w-5 h-5 mr-2" />
                ) : (
                  <CreditCard className="w-5 h-5 mr-2" />
                )}
                {loading
                  ? 'Processing...'
                  : paymentMethod === 'COD'
                  ? `Place Order (COD)`
                  : `Pay ₹${state.totalPrice.toLocaleString('en-IN')}`}
              </button>
              
              <p className="text-xs text-gray-500 text-center mt-4 flex items-center justify-center">
                {paymentMethod === 'COD' ? 'Fulfilled by Shiprocket COD' : 'Secured by Razorpay'}
              </p>
            </div>
          </div>
        </div>
      </div>

      <AuthModal 
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onSuccess={() => {
          setShowAuthModal(false);
          // Small delay to ensure state updates
          setTimeout(() => {
            handleCheckout();
          }, 100);
        }}
      />

      <AddressModal
        isOpen={showAddressModal}
        onClose={() => setShowAddressModal(false)}
        onSuccess={(savedAddress) => {
          setShowAddressModal(false);
          const updatedShippingDetails = { ...shippingDetails, ...savedAddress };
          setShippingDetails(updatedShippingDetails);
          // Small delay to ensure state updates
          setTimeout(() => {
            handleCheckout(false, updatedShippingDetails);
          }, 100);
        }}
      />

      <DuplicateOrderModal
        isOpen={showDuplicateModal}
        onClose={() => setShowDuplicateModal(false)}
        duplicateInfo={duplicateInfo || { success: false, hasDuplicate: false }}
        onProceedAnyway={() => {
          setShowDuplicateModal(false);
          handleCheckout(true);
        }}
        loading={loading}
      />
    </div>
  );
};

export default CheckoutPage;
